'use client';

import React, { useState, useRef } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faUserGroup,
  faCheck,
  faTimes,
  faUsers,
  faPlus,
} from '@fortawesome/pro-solid-svg-icons';
import { Typography, Button, Dialog } from 'src/components';
import { useGetAllCompanyUsersQuery } from 'src/api/redux';
import { useAuth } from 'src/context/auth';

interface User {
  id: string | number;
  first_name: string;
  last_name: string;
  username: string;
}

interface UserMultiSelectProps {
  selectedUsers: string[];
  onChange: (users: string[]) => void;
}

const UserMultiSelect: React.FC<UserMultiSelectProps> = ({
  selectedUsers,
  onChange,
}) => {
  const { user } = useAuth();
  const companyId = user?.company?.id?.toString();
  const { data: allUsers, isLoading } = useGetAllCompanyUsersQuery(
    { companyId },
    { skip: !companyId }
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);

  // Filter users based on search term
  const filteredUsers =
    allUsers?.filter((user: User) => {
      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
      const username = user.username.toLowerCase();
      const search = searchTerm.toLowerCase();

      return fullName.includes(search) || username.includes(search);
    }) || [];

  // Toggle user selection
  const toggleUserSelection = (userId: string) => {
    const userIdStr = String(userId);
    if (selectedUsers.some((id) => String(id) === userIdStr)) {
      onChange(selectedUsers.filter((id) => String(id) !== userIdStr));
    } else {
      onChange([...selectedUsers, userIdStr]);
    }
  };

  // Remove a selected user
  const removeUser = (userId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const userIdStr = String(userId);
    onChange(selectedUsers.filter((id) => String(id) !== userIdStr));
  };

  // Select all users
  const selectAllUsers = () => {
    if (!allUsers) return;
    const allUserIds = allUsers.map((u: User) => String(u.id));
    onChange(allUserIds);
  };

  // Clear all selected users
  const clearAllUsers = () => {
    onChange([]);
  };

  return (
    <div className="relative">
      <div className="flex justify-between items-center mb-1">
        <label className="block text-sm font-medium text-neutral-700">
          Team Members
        </label>
        <Button
          variant="plain"
          color="primary"
          size="sm"
          startDecorator={<Icon icon={faPlus} />}
          onClick={() => setIsModalOpen(true)}
          className="text-xs"
        >
          Add Members
        </Button>
      </div>

      <Typography className="text-xs text-neutral-500 mb-1">
        Click in the box below to add or edit team members
      </Typography>

      {/* Input field that opens the modal */}
      <div
        ref={inputRef}
        onClick={() => setIsModalOpen(true)}
        className="flex flex-col gap-2 min-h-[42px] border border-neutral-300 rounded-md p-2 bg-white cursor-text"
      >
        {selectedUsers.length === 0 ? (
          <div className="flex items-center text-neutral-500 text-sm">
            <Icon icon={faUserGroup} className="mr-2 text-neutral-400" />
            Click to select team members
          </div>
        ) : (
          <div className="flex flex-col gap-2 w-full">
            {selectedUsers.map((userId) => {
              const userObj = allUsers?.find(
                (u: User) => String(u.id) === String(userId)
              );
              if (!userObj) {
                return null;
              }

              return (
                <div
                  key={userId}
                  className="flex items-center justify-between p-2 border border-neutral-200 rounded-md"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-neutral-100 rounded-full flex items-center justify-center text-sm text-neutral-700 font-medium">
                      {userObj.first_name.charAt(0)}
                      {userObj.last_name.charAt(0)}
                    </div>
                    <div className="flex flex-col">
                      <Typography className="text-sm font-medium text-neutral-800">
                        {userObj.first_name} {userObj.last_name}
                      </Typography>
                      <Typography className="text-xs text-neutral-500">
                        {userObj.username}
                      </Typography>
                    </div>
                  </div>
                  <button
                    onClick={(e) => removeUser(userId, e)}
                    className="text-neutral-400 hover:text-red-500"
                  >
                    <Icon icon={faTimes} className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* User selection modal */}
      <Dialog
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        className="w-full max-w-md"
        size="xl"
      >
        <Dialog.Header>
          <div className="flex items-center justify-between w-full">
            <Typography variant="h5" className="text-neutral-800">
              Select Team Members
            </Typography>
            <div className="flex gap-2">
              <Button
                variant="plain"
                color="primary"
                size="sm"
                startDecorator={<Icon icon={faUsers} />}
                onClick={selectAllUsers}
              >
                Select All
              </Button>
              <Button
                variant="plain"
                color="neutral"
                size="sm"
                onClick={clearAllUsers}
              >
                Clear All
              </Button>
            </div>
          </div>
        </Dialog.Header>

        <Dialog.Content>
          <div className="p-2">
            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon icon={faSearch} className="text-neutral-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-neutral-300 rounded-md text-sm placeholder-neutral-400 focus:outline-none focus:ring-primary focus:border-primary"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>

            <div className="max-h-[300px] overflow-y-auto">
              {isLoading ? (
                <div className="text-center py-4">
                  <Typography className="text-sm text-neutral-500">
                    Loading users...
                  </Typography>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-4">
                  <Typography className="text-sm text-neutral-500">
                    No users found
                  </Typography>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2">
                  {filteredUsers.map((user: User) => (
                    <div
                      key={user.id}
                      className={`flex items-center justify-between p-2 hover:bg-neutral-50 cursor-pointer rounded-md ${
                        selectedUsers.includes(String(user.id))
                          ? 'bg-sky-50'
                          : ''
                      }`}
                      onClick={() => toggleUserSelection(String(user.id))}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-neutral-100 rounded-full flex items-center justify-center text-sm text-neutral-700 font-medium">
                          {user.first_name.charAt(0)}
                          {user.last_name.charAt(0)}
                        </div>
                        <div className="flex flex-col">
                          <Typography className="text-sm font-medium text-neutral-800">
                            {user.first_name} {user.last_name}
                          </Typography>
                          <Typography className="text-xs text-neutral-500">
                            {user.username}
                          </Typography>
                        </div>
                      </div>
                      {selectedUsers.includes(String(user.id)) && (
                        <Icon icon={faCheck} className="text-sky-600 h-4 w-4" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Dialog.Content>

        <Dialog.Actions>
          <div className="flex justify-end gap-2 w-full">
            <Button
              variant="outlined"
              color="neutral"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="primary"
              onClick={() => setIsModalOpen(false)}
            >
              Done
            </Button>
          </div>
        </Dialog.Actions>
      </Dialog>
    </div>
  );
};

export default UserMultiSelect;
